import { createRef } from 'react';
import { Button, Input } from '@aqarmap/aqarmap-ui';
import Modal from '~/components/Modal';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { objectSerialize } from '~/lib';
import {
  useSendOtpMutation,
  useVerifyOtpMutation,
} from '~/redux/api/authApiSlice';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { closeModal } from '~/redux/features/uiState/uiStateSlice';
import { useNavigate } from 'react-router-dom';
import '~/components/OtpModal/styles.css';
import useQuery from '~/hooks/useQuery';

export default function OtpModal({
  title,
  phone,
  redirectPage,
  description,
}: {
  title: string;
  phone: string;
  redirectPage?: string;
  description: React.ReactNode;
}) {
  const { redirectTo } = useQuery();
  const submitBtnRef = createRef<HTMLButtonElement>();
  const otpMaxLength = 6;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { t } = useTranslation('app');
  const [value, setValue] = useState<string>('');
  const [timer, setTimer] = useState<number>(60);
  const [isDisabled, setIsDisable] = useState<boolean>(true);
  const [isLoadingResendOTP, setIsLoadingResendOTP] = useState<boolean>(false);
  const [sendOtp] = useSendOtpMutation();
  const [verifyOtp, { isError, error, isLoading, reset }] =
    useVerifyOtpMutation();
  const { modals } = useAppSelector((state) => state.uiState);

  const handleVerifyOtp = async () => {
    const formData = objectSerialize({ otp: value });
    const res = await verifyOtp(formData);
    if (res.data && !res.error) {
      if (redirectTo) {
        window.location.href = redirectTo;
      } else {
        dispatch(closeModal('otpModal'));
        if (redirectPage) return navigate(redirectPage);
      }
    }
  };

  const handleResendOTP = async () => {
    setTimer(60);
    const formData = objectSerialize({ temporaryPhone: phone });
    const res = await sendOtp(formData);
    if (res.data && !res.error) {
      setIsLoadingResendOTP(false);
    }
  };

  const handleOnKeyUp = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const isDeleteAction = event.key === 'Delete' || event.key === 'Backspace';
    const value = event.currentTarget.value;
    if (!isDeleteAction && value.length === otpMaxLength) {
      submitBtnRef?.current?.focus();
    }
  };

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setValue(event.target.value);
    if (value.length === otpMaxLength) {
      setIsDisable(false);
    } else {
      setIsDisable(true);
    }
  };

  useEffect(() => {
    if (modals.otpModal) {
      reset();
      setValue('');
      setTimer(60);
      const interval = setInterval(() => {
        setTimer((prevTimer) => (prevTimer > 0 ? prevTimer - 1 : 0));
      }, 1000);
      return () => clearInterval(interval);
    }
  }, [modals]);

  return (
    <Modal title={title} modalKey="otpModal">
      <div className="flex flex-col gap-3x pb-3x w-full">
        <div className="flex flex-col gap-0.5x">
          {description}
          <p className="text-body_1 text-gray__dark_2">{phone}</p>
        </div>
        <Input
          className="otp-container element-center w-full"
          field={{
            onKeyUp: handleOnKeyUp,
            onChange: handleOnChange,
            value,
            id: 'otp',
            name: 'otp',
            type: 'text',
            maxLength: otpMaxLength,
          }}
          error={isError ? t((error as any)?.data?.message) : ''}
        />

        <div className="flex flex-col gap-0.5x justify-center items-center">
          <p className="text-body_2 text-gray__dark_1">
            {timer === 60 ? '01:00' : `00:${timer}`}
          </p>
          <Button
            ariaLabel="resendOtp"
            variant="link"
            name="link"
            className={`hover:!bg-white !no-underline h-[24px] ${timer === 0 ? '' : 'cursor-auto'}`}
            disabled={timer > 0}
            onClick={handleResendOTP}
            isLoading={isLoadingResendOTP}
            size="small"
          >
            <p
              className={`text-link !p-0 !m-0 ${timer === 0 ? 'text-blue' : 'text-gray'}`}
            >
              {t('resendOTP')}
            </p>
          </Button>
        </div>
        <Button
          disabled={isDisabled || isLoading}
          ref={submitBtnRef}
          size="medium"
          variant="primary"
          onClick={handleVerifyOtp}
          isLoading={isLoading}
        >
          {t('send')}
        </Button>
      </div>
    </Modal>
  );
}
